// contexts/LocaleContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { saveLocale, getLocaleFromCookie, checkServerLocale } from '../utils/locale';
import { AuthContext } from './AuthContext';
import i18n from '../i18n';

export const LocaleContext = createContext();

export const LocaleProvider = ({ children }) => {
    const [locale, setLocale] = useState(null);
    const [updateKey, setUpdateKey] = useState(0);
    const token = useContext(AuthContext);

    useEffect(() => {
        const initLocale = async () => {
            const waitForI18n = () => {
                return new Promise((resolve) => {
                    if (i18n.isInitialized) {
                        resolve();
                    } else {
                        i18n.on('initialized', () => {
                            resolve();
                        });
                    }
                });
            };
            await waitForI18n();
            const cookieLocale = getLocaleFromCookie();
            if (cookieLocale) {
                setLocale(cookieLocale);
                const i18nLocale = cookieLocale.toLowerCase();
                i18n.changeLanguage(i18nLocale);
                if (token) {
                    const serverLocale = await checkServerLocale(token);
                    if (serverLocale && serverLocale !== cookieLocale) {
                        setLocale(serverLocale);
                        saveLocale(serverLocale);
                        const serverI18nLocale = serverLocale.toLowerCase();
                        i18n.changeLanguage(serverI18nLocale);
                    }
                }
            } else {
                setLocale('EN');
                saveLocale('EN');
                i18n.changeLanguage('en');
            }
        };
        initLocale();
    }, [token]);

    const updateLocale = (newLocale) => {
        if (newLocale) {
            setLocale(newLocale);
            saveLocale(newLocale);
            const i18nLocale = newLocale.toLowerCase();
            i18n.changeLanguage(i18nLocale);
            localStorage.setItem('i18nextLng', i18nLocale);
            document.documentElement.lang = i18nLocale;
            setUpdateKey(prev => prev + 1);
        }
    };

    return (
        <LocaleContext.Provider value={{ locale, updateLocale, updateKey }}>
            {children}
        </LocaleContext.Provider>
    );
};

export const useLocale = () => {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocaleProvider');
    }
    return context;
}; 